import React, { useEffect, useMemo, useState } from 'react'
import { Tour, TourProps } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { updateTourProgress } from '../../../store/session/action-creators'
import { TOURS_ENABLED } from '../tours-enabled'

type Props = {
  isNew: boolean
}

const CREATE_TOUR_ID = 'tour.appeals.create.v1'

function findFieldControlByName(fieldName: string): HTMLElement | null {
  const escaped = String(fieldName).replace(/"/g, '\\"')
  const byDataField = document.querySelector(`[data-field-name="${escaped}"]`) as HTMLElement | null
  const node =
    byDataField ||
    (document.querySelector(`[name*="${escaped}"]`) as HTMLElement | null) ||
    (document.querySelector(`[id*="${escaped}"]`) as HTMLElement | null)

  if (!node) return null

  // data-field-name устанавливается на обёртку <div> вокруг поля.
  // С неё находим фактический antd Form.Item/control.
  const container = byDataField || node
  const formItem =
    (container.querySelector('.ant-form-item') as HTMLElement | null) ||
    (container.closest('.ant-form-item') as HTMLElement | null) ||
    (container.classList?.contains('ant-form-item') ? container : null)

  if (!formItem) return node

  const control = formItem.querySelector('.ant-form-item-control') as HTMLElement | null
  if (!control) return formItem

  const preferred = control.querySelector(
    [
      // antd Select
      '.ant-select-selector',
      '.ant-select',
      // antd Input
      'input',
      'textarea',
      '.ant-input',
      // fallback
      '[contenteditable="true"]',
    ].join(','),
  ) as HTMLElement | null

  return preferred || control || formItem
}

function findFieldContainerByName(fieldName: string): HTMLElement | null {
  const escaped = String(fieldName).replace(/"/g, '\\"')
  const byDataField = document.querySelector(`[data-field-name="${escaped}"]`) as HTMLElement | null
  if (byDataField) {
    return (
      (byDataField.querySelector('.ant-form-item') as HTMLElement | null) ||
      (byDataField.closest('.ant-form-item') as HTMLElement | null) ||
      byDataField
    )
  }
  const node =
    (document.querySelector(`[name*="${escaped}"]`) as HTMLElement | null) ||
    (document.querySelector(`[id*="${escaped}"]`) as HTMLElement | null)
  if (!node) return null
  return (node.closest('.ant-form-item') as HTMLElement | null) || node
}

function safeTarget(getter: () => HTMLElement | null): () => HTMLElement {
  return () => getter() || document.body
}

async function waitForElement(
  getter: () => HTMLElement | null,
  timeoutMs = 2500,
): Promise<boolean> {
  const start = Date.now()
  if (getter()) return true

  return await new Promise(resolve => {
    const tick = () => {
      if (getter()) return resolve(true)
      if (Date.now() - start >= timeoutMs) return resolve(false)
      window.setTimeout(tick, 50)
    }
    tick()
  })
}

export function AppealCreateOnboardingTour({ isNew }: Props) {
  const dispatch = useDispatch()
  const sessionData = useSelector((s: any) => s.session?.sessionData)
  const tours = (sessionData as any)?.jsonSettings?.tours || {}
  const progress = tours?.[CREATE_TOUR_ID] || {}

  const completed = Boolean(progress?.completed)
  const shouldOpen = Boolean(progress?.stage === 'prompted' || progress?.stage === 'in_progress')

  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState<number>(Number(progress?.currentStep) || 0)

  // Пытаемся фокусироваться на активном поле при смене шага.
  useEffect(() => {
    if (!open) return
    const map: Record<number, string | null> = {
      1: 'source_appeal_id',
      2: 'phone',
      3: 'name',
    }
    const field = map[current]
    if (!field) return
    // даём время для рендера и анимации
    const t = window.setTimeout(() => {
      const el = findFieldControlByName(field)
      if (!el) return
      // Приоритет фокуса на нативном input
      const input = el.querySelector('input, textarea') as
        | HTMLInputElement
        | HTMLTextAreaElement
        | null
      if (input?.focus) {
        input.focus()
        return
      }
      const focus = (el as any)?.focus
      if (typeof focus === 'function') {
        focus.call(el)
      }
    }, 50)
    return () => window.clearTimeout(t)
  }, [current, open])

  useEffect(() => {
    if (!isNew) return
    if (completed) return
    if (!shouldOpen) return
    ;(async () => {
      // Дождаться рендера поля, чтобы Tour смог корректно сфокусироваться.
      await waitForElement(() => findFieldControlByName('source_appeal_id'), 3000)
      setOpen(true)
    })()
  }, [isNew, completed, shouldOpen])

  const steps: TourProps['steps'] = useMemo(
    () => [
      {
        title: 'Создание обращения',
        description:
          'Это форма нового обращения. Заполните ключевые поля — система автоматически сохранит обращение и оно появится в списке.',
        target: () => null,
        placement: 'center',
      },
      {
        title: 'Вид контакта',
        description:
          'Выберите «Вид контакта» — это обязательное поле. Оно влияет на аналитику и дальнейшую обработку обращения.',
        target: safeTarget(() => findFieldContainerByName('source_appeal_id')),
      },
      {
        title: 'Телефон клиента',
        description:
          'Укажите телефон — так мы сможем быстро найти клиента, избежать дублей и корректно связать обращения между собой.',
        target: safeTarget(() => findFieldContainerByName('phone')),
      },
      {
        title: 'Контактное лицо клиента',
        description:
          'Заполните контактное лицо — это ускоряет коммуникацию и помогает коллегам понимать, с кем именно нужно связаться.',
        target: safeTarget(() => findFieldContainerByName('name')),
      },
    ],
    [],
  )

  const persist = async (step: number) => {
    await dispatch(
      updateTourProgress(CREATE_TOUR_ID, { stage: 'in_progress', currentStep: step }) as any,
    )
  }

  const onClose = async () => {
    setOpen(false)
    await dispatch(
      updateTourProgress(CREATE_TOUR_ID, { stage: 'closed', currentStep: current }) as any,
    )
  }

  const onFinish = async () => {
    setOpen(false)
    await dispatch(
      updateTourProgress(CREATE_TOUR_ID, {
        stage: 'waiting_save',
        completed: false,
        currentStep: steps.length - 1,
      }) as any,
    )
  }

  const onChange = async (next: number) => {
    const clamped = Math.max(0, Math.min(next, steps.length - 1))
    setCurrent(clamped)
    await persist(clamped)
  }

  if (!TOURS_ENABLED || !isNew || completed) return null

  return (
    <Tour
      open={open}
      current={current}
      onClose={onClose}
      onFinish={onFinish}
      onChange={onChange}
      steps={steps}
    />
  )
}
